
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useSettingsStore } from "@/store/api/settingsStore";
import { toast } from "sonner";

export function LanguageTab() {
  const { t, i18n } = useTranslation("settings");
  const tCommon = (key: string) => t(key, { ns: "common" });
  const { settings, updateSettings, isLoading, fetchSettings } = useSettingsStore();
  
  const [defaultLanguage, setDefaultLanguage] = useState(settings.language.defaultLanguage);
  const [currency, setCurrency] = useState<"EUR" | "TND" | "SAR">(settings.language.currency);
  
  // Fetch settings when component mounts
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Update local state when settings change
  useEffect(() => {
    setDefaultLanguage(settings.language.defaultLanguage);
    setCurrency(settings.language.currency);
  }, [settings]);

  const handleLanguageChange = (value: string) => {
    setDefaultLanguage(value);
  };

  const handleCurrencyChange = (value: "EUR" | "TND" | "SAR") => {
    setCurrency(value);
  };

  const handleSwitchLanguage = (value: string) => {
    i18n.changeLanguage(value);
  };

  const handleSave = async () => {
    try {
      await updateSettings({
        language: {
          defaultLanguage,
          currency
        }
      });
      toast.success(tCommon('saveSuccess'));
    } catch (error) {
      toast.error(tCommon('saveFailed'));
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('language.title')}</CardTitle>
          <CardDescription>{t('language.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="default-language">{t('language.defaultLanguage')}</Label>
              <Select value={defaultLanguage} onValueChange={handleLanguageChange}>
                <SelectTrigger id="default-language">
                  <SelectValue placeholder={t('language.selectDefaultLanguage')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="currency">{t('language.currency')}</Label>
              <Select value={currency} onValueChange={(val) => handleCurrencyChange(val as "EUR" | "TND" | "SAR")}>
                <SelectTrigger id="currency">
                  <SelectValue placeholder={t('language.selectCurrency')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">Euro (€)</SelectItem>
                  <SelectItem value="TND">Tunisian Dinar (TND)</SelectItem>
                  <SelectItem value="SAR">Saudi Riyal (SAR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? tCommon('saving') : tCommon('save')}
          </Button>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>{t('language.quickSwitch')}</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup value={i18n.language} onValueChange={handleSwitchLanguage}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="en" id="en" />
              <Label htmlFor="en">English</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="fr" id="fr" />
              <Label htmlFor="fr">Français</Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>
    </div>
  );
}
